import {
  EnumPaymentType,
  EnumTransactionTimelineStepStatus,
  type EnumTransactionTimelineStepType,
  EnumVerificationType,
} from "@/api/cashier";
import { useTranslation } from "react-i18next";
import styles from "./TransactionTimeline.module.css";

/* The TransactionStepTypeLabel component
  This component displays a label for a transaction step based on its type and status.
  @param stepType - The type of the transaction step.
  @param stepStatus - The status of the transaction step.
  @param paymentType - (Optional) The type of payment associated with the transaction.
  @param estimatedCompletion - (Optional) Estimated completion time in days for certain steps.
  @param isPendingConfirmation - (Optional) Whether this step is awaiting 2FA confirmation.
  @param countdownTime - (Optional) Formatted countdown string for OTP steps.
  @param verificationType - (Optional) The verification type when pending confirmation.
  @returns A ReactElement representing the label for the transaction step.
*/
export const TransactionStepTypeLabel = ({
  stepType,
  stepStatus,
  paymentType,
  estimatedCompletion,
  isPendingConfirmation,
  countdownTime,
  verificationType,
}: {
  stepType: EnumTransactionTimelineStepType;
  stepStatus: EnumTransactionTimelineStepStatus;
  paymentType?: EnumPaymentType;
  estimatedCompletion?: number | null;
  isPendingConfirmation?: boolean;
  countdownTime?: string;
  verificationType?: EnumVerificationType;
}) => {
  let text = "";
  const { t } = useTranslation();
  const className = stepStatus === "not_started" ? styles.inactive : "";

  switch (stepType) {
    case "request_submitted":
      if (isPendingConfirmation) {
        if (verificationType === EnumVerificationType.EnumVerificationTypeOtp) {
          text = t(
            "transactions.history.timeline.steps.pending_confirmation_otp",
            { time: countdownTime ?? "" }
          );
        } else if (
          verificationType === EnumVerificationType.EnumVerificationTypeEmail
        ) {
          text = t(
            "transactions.history.timeline.steps.pending_confirmation_email"
          );
        } else {
          text = t("transactions.history.timeline.steps.pending_confirmation");
        }
      } else {
        text =
          paymentType === EnumPaymentType.EnumPaymentTypeWithdrawal &&
          stepStatus ===
            EnumTransactionTimelineStepStatus.EnumTransactionTimelineStepStatusInProgress
            ? t("transactions.history.timeline.steps.requested")
            : t("transactions.history.timeline.steps.request_submitted");
      }
      break;
    case "pending_review":
      text = estimatedCompletion
        ? t("transactions.history.timeline.steps.pending_review_days", {
            days: estimatedCompletion,
          })
        : t("transactions.history.timeline.steps.pending_review");
      break;
    case "pending_for_funds":
      text = t("transactions.history.timeline.steps.pending_for_funds");
      break;
    case "processing":
      text = t("transactions.history.timeline.steps.processing");
      break;
    case "complete":
      text =
        paymentType === "deposit" && stepStatus === "completed"
          ? t("transactions.history.timeline.steps.completed_deposit")
          : t("transactions.history.timeline.steps.complete");
      break;
    case "unsuccessful":
      text =
        paymentType !== "transfer"
          ? t("transactions.history.timeline.steps.unsuccessful_email_info")
          : t("transactions.history.timeline.steps.unsuccessful");
      break;
    case "cancelled":
      text = t("transactions.history.timeline.steps.cancelled_email_info");
      break;
    case "cancelled_by_user":
      text = t("transactions.history.timeline.steps.cancelled_as_requested");
      break;
    case "cancelled_due_inactivity":
      text = t(
        "transactions.history.timeline.steps.cancelled_due_to_inactivity"
      );
      break;
  }
  return <p className={className}>{text}</p>;
};
